import { Box, Chip, Paper, Typography, useTheme } from "@mui/material";
import { PageHeader } from "../components/PageHeader";
import { CHANGELOG_ENTRIES } from "../content/changelog";
import type { ChangelogCategory } from "../content/changelog.types";

const CATEGORY_LABELS: Record<ChangelogCategory, string> = {
  added: "Dodano",
  improved: "Poboljšano",
  fixed: "Ispravljeno",
  security: "Sigurnost",
};

function categoryColor(
  kind: ChangelogCategory,
): "success" | "warning" | "info" | "error" {
  switch (kind) {
    case "added":
      return "success";
    case "improved":
      return "warning";
    case "fixed":
      return "info";
    case "security":
      return "error";
    default:
      return "info";
  }
}

function formatDate(date: string): string {
  const parsed = new Date(`${date}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString("hr-HR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function ChangelogPage() {
  const theme = useTheme();

  return (
    <Box>
      <PageHeader
        title="Promjene"
        subtitle="Bilješke o izdanjima i novostima Savezi sustava. Ovdje pratite što je dodano, poboljšano ili ispravljeno."
      />

      <Box sx={{ position: "relative" }}>
        {CHANGELOG_ENTRIES.map((entry, index) => (
          <Box
            key={entry.version}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 2, md: 4 },
              mb: 4,
              position: "relative",
            }}
          >
            <Box
              sx={{
                width: { md: 150 },
                flexShrink: 0,
                textAlign: { md: "right" },
                pr: { md: 3 },
                position: "relative",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontFamily: "monospace", fontWeight: 600 }}
              >
                v{entry.version}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(entry.date)}
              </Typography>
              <Box
                sx={{
                  display: { xs: "none", md: "block" },
                  position: "absolute",
                  right: -6,
                  top: 6,
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: index === 0 ? "primary.main" : "action.disabled",
                  boxShadow: `0 0 0 4px ${theme.palette.background.default}`,
                }}
              />
            </Box>

            <Paper variant="outlined" sx={{ flex: 1, p: { xs: 2, sm: 3 } }}>
              {entry.groups.map((group) => (
                <Box key={group.kind} sx={{ mb: 3, "&:last-child": { mb: 0 } }}>
                  <Chip
                    size="small"
                    color={categoryColor(group.kind)}
                    label={CATEGORY_LABELS[group.kind]}
                    sx={{
                      mb: 1.5,
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}
                  />
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {group.items.map((item) => (
                      <Typography
                        component="li"
                        key={item}
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.75 }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ))}
            </Paper>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
